// Tick model and frame mapping (docs/03-architecture.md, F-02.9, D-19, D-20).
// A tick is the smallest timeline unit: tickRate = A fps × the largest integer interpolation ratio among the B's.

import type { FrameMapping, SourceId } from './types';

export interface TimelineSource {
  id: SourceId;
  frameCount: number;
  frameRate: number;
  frameMapping: FrameMapping;
  frameOffset: number;
  /** Presentation timestamps (seconds, relative to the first frame) for time-based mapping; optional. */
  timestamps?: ArrayLike<number>;
}

export interface Timeline {
  /** Ticks per A frame. */
  ticksPerFrame: number;
  /** Up to the end of the longest source (D-26): A or a B that runs past A. */
  totalTicks: number;
  aFrameCount: number;
  aFrameRate: number;
  /** Effective mapping per source after resolving incompatible ratios. */
  effective: Map<SourceId, FrameMapping>;
}

export interface SourceFrameAt {
  /**
   * Frame index shown for the source. Outside its range the source holds its nearest end frame (the last one after
   * it ends, the first one before it starts; D-26) and `held` is true. Null only for a source without frames.
   */
  frame: number | null;
  /** Outside the source's own range: the frame shown is held, the pane says "out of range" (D-26). */
  held: boolean;
  /** The unclamped frame index (useful for "out of range" labelling). */
  rawFrame: number;
  /** For ratio mapping: sub-frame index within the A frame; 0 = has an original counterpart. */
  sub: number;
  /** True for A when the tick falls on an interpolated position (A shows the previous original frame). */
  noCounterpart: boolean;
  /** True when this source's mapping is time-based (not frame-accurate). */
  approximate: boolean;
}

export function buildTimeline(sources: TimelineSource[]): Timeline {
  const a = sources.find((s) => s.id === 'A');
  if (!a) throw new Error('Timeline needs source A');
  const ratios = sources
    .filter((s) => s.id !== 'A' && s.frameMapping.mode === 'ratio')
    .map((s) => (s.frameMapping as { ratio: number }).ratio);
  const maxRatio = ratios.length ? Math.max(...ratios) : 1;
  const effective = new Map<SourceId, FrameMapping>();
  for (const s of sources) {
    if (s.id === 'A') {
      effective.set('A', { mode: 'one-to-one' });
    } else if (s.frameMapping.mode === 'ratio' && maxRatio % s.frameMapping.ratio !== 0) {
      // Ratios must divide the largest ratio; otherwise fall back to time-based (F-02.9).
      effective.set(s.id, { mode: 'time' });
    } else {
      effective.set(s.id, s.frameMapping);
    }
  }
  const tl: Timeline = {
    ticksPerFrame: maxRatio,
    totalTicks: Math.max(1, a.frameCount * maxRatio),
    aFrameCount: a.frameCount,
    aFrameRate: a.frameRate,
    effective,
  };
  // Playback and the time bar run to the end of the longest source; a B that ends later extends the timeline.
  for (const s of sources) if (s.id !== 'A') tl.totalTicks = Math.max(tl.totalTicks, Math.round(rawCoverage(tl, s).end));
  return tl;
}

export function aFrameOfTick(tl: Timeline, tick: number): { frame: number; sub: number } {
  const t = Math.max(0, Math.floor(tick));
  return { frame: Math.floor(t / tl.ticksPerFrame), sub: t % tl.ticksPerFrame };
}

export function tickOfAFrame(tl: Timeline, frame: number): number {
  return frame * tl.ticksPerFrame;
}

/** Nearest frame index for a time (seconds) given sorted timestamps, or nominal rate when absent. */
export function frameAtTime(sec: number, frameRate: number, frameCount: number, timestamps?: ArrayLike<number>): number {
  if (!timestamps || timestamps.length === 0) return Math.round(sec * frameRate);
  // Binary search for the last timestamp <= sec + half a frame, then pick the nearest neighbour.
  let lo = 0;
  let hi = timestamps.length - 1;
  if (sec <= timestamps[0]) return sec < timestamps[0] - 1 / frameRate ? -1 : 0;
  if (sec >= timestamps[hi]) return sec > timestamps[hi] + 1 / frameRate ? frameCount : hi;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (timestamps[mid] <= sec) lo = mid;
    else hi = mid;
  }
  return sec - timestamps[lo] <= timestamps[hi] - sec ? lo : hi;
}

export function sourceFrameAt(tl: Timeline, src: TimelineSource, tick: number): SourceFrameAt {
  const { frame: aFrame, sub } = aFrameOfTick(tl, tick);
  const mapping = tl.effective.get(src.id) ?? src.frameMapping;
  let raw: number;
  let subOut = 0;
  let noCounterpart = false;
  let approximate = false;
  if (src.id === 'A') {
    raw = aFrame;
    noCounterpart = sub !== 0;
  } else if (mapping.mode === 'one-to-one') {
    raw = aFrame + src.frameOffset;
  } else if (mapping.mode === 'ratio') {
    subOut = Math.floor((sub * mapping.ratio) / tl.ticksPerFrame);
    raw = aFrame * mapping.ratio + subOut + src.frameOffset;
  } else {
    approximate = true;
    const sec = (aFrame + sub / tl.ticksPerFrame) / tl.aFrameRate;
    // The offset (in B frames) shifts time before the lookup, so the out-of-range result stays out of range (D-26).
    raw = frameAtTime(sec + src.frameOffset / src.frameRate, src.frameRate, src.frameCount, src.timestamps);
  }
  return holdFrame(raw, src.frameCount, { rawFrame: raw, sub: subOut, noCounterpart, approximate });
}

/** The frame shown for a raw index: itself in range, else the nearest end frame, held (D-26). */
export function holdFrame(raw: number, frameCount: number, rest: Omit<SourceFrameAt, 'frame' | 'held'>): SourceFrameAt {
  if (frameCount <= 0) return { ...rest, frame: null, held: true };
  const inRange = raw >= 0 && raw < frameCount;
  return { ...rest, frame: inRange ? raw : raw < 0 ? 0 : frameCount - 1, held: !inRange };
}

/** Last A frame index for a tick (the timeline can run past A's end while a longer B plays; A holds its last frame). */
export function heldAFrame(tl: Timeline, tick: number): number {
  return Math.min(tl.aFrameCount - 1, aFrameOfTick(tl, tick).frame);
}

/** Covered tick range [start, end) of a B on the timeline (F-02.10). */
export function coverage(tl: Timeline, src: TimelineSource): { start: number; end: number } {
  const r = rawCoverage(tl, src);
  return {
    start: Math.max(0, Math.round(r.start)),
    end: Math.min(tl.totalTicks, Math.round(r.end)),
  };
}

/** Unclamped tick range of a source (A: its own frames). */
function rawCoverage(tl: Timeline, src: TimelineSource): { start: number; end: number } {
  const mapping = tl.effective.get(src.id) ?? src.frameMapping;
  if (src.id === 'A') return { start: 0, end: tl.aFrameCount * tl.ticksPerFrame };
  let startTick: number;
  let endTick: number;
  if (mapping.mode === 'one-to-one') {
    startTick = -src.frameOffset * tl.ticksPerFrame;
    endTick = (src.frameCount - src.frameOffset) * tl.ticksPerFrame;
  } else if (mapping.mode === 'ratio') {
    const ticksPerB = tl.ticksPerFrame / mapping.ratio;
    startTick = -src.frameOffset * ticksPerB;
    endTick = (src.frameCount - src.frameOffset) * ticksPerB;
  } else {
    const ticksPerSec = tl.aFrameRate * tl.ticksPerFrame;
    startTick = (-src.frameOffset / src.frameRate) * ticksPerSec;
    endTick = ((src.frameCount - src.frameOffset) / src.frameRate) * ticksPerSec;
  }
  return { start: startTick, end: endTick };
}

/** Next/previous tick when stepping; with originalOnly, snaps to ticks that have an A original frame. */
export function stepTick(tl: Timeline, tick: number, delta: number, originalOnly: boolean): number {
  let t: number;
  if (originalOnly && tl.ticksPerFrame > 1) {
    const { frame } = aFrameOfTick(tl, tick);
    const base = frame * tl.ticksPerFrame;
    if (delta > 0) t = base + delta * tl.ticksPerFrame;
    else t = tick === base ? base + delta * tl.ticksPerFrame : base + (delta + 1) * tl.ticksPerFrame;
  } else {
    t = tick + delta;
  }
  return clampTick(tl, t);
}

export function clampTick(tl: Timeline, tick: number): number {
  return Math.min(tl.totalTicks - 1, Math.max(0, Math.round(tick)));
}

/** "F 120 +1/2" style label (F-02.9). base is the display frame-number base (D-35). */
export function frameLabel(tl: Timeline, tick: number, base: 0 | 1): string {
  const { frame, sub } = aFrameOfTick(tl, tick);
  const n = frame + base;
  return sub === 0 ? `${n}` : `${n} +${sub}/${tl.ticksPerFrame}`;
}

/** Seconds per tick for real-time playback. */
export function tickDurationSec(tl: Timeline): number {
  return 1 / (tl.aFrameRate * tl.ticksPerFrame);
}

/**
 * Converts a tick range when the number of ticks per A frame changes (e.g. an interpolated B is added):
 * a range keeps covering the same A frames / generated positions.
 */
export function rescaleTickRange(start: number, end: number, from: number, to: number): { start: number; end: number } {
  if (from === to || from <= 0 || to <= 0) return { start, end };
  const s = Math.floor((start * to) / from);
  const e = Math.max(s, Math.ceil(((end + 1) * to) / from) - 1);
  return { start: s, end: e };
}
